import React, { useEffect, useState } from 'react'
import SubHeader from '../Shared/Display/SubHeader'
import DateTimeFilter from '../Shared/Buttons/DateTimeFilter'
import CustomFilter from '../Shared/Buttons/CustomFilter'
import { useDispatch, useSelector } from 'react-redux'
import { fetchOverview } from '../Redux/Actions/Overview'
import { fetchMonthlyActiveUsers } from '../Redux/Actions/MonthlyActiveUsers'
import { dateDelta } from '../Shared/timeutils'
import './Overview.css'
import { componentCreated, updateDateFilter } from '../Redux/Actions/DateFilter'
import { fetchProfiles, fetchGroups } from '../Redux/Actions/FilterLists'
import ContentBlock from '../Shared/Data/DashboardItems/ContentBlock'
import AdministratorBlock from '../Shared/Data/DashboardItems/AdministratorBlock'
import SocialBlock from '../Shared/Data/DashboardItems/SocialBlock'
import IndividualBlock from '../Shared/Data/DashboardItems/IndividualBlock'
import MonthlyActiveUsers from '../Shared/Data/DashboardItems/MonthlyActiveUsers'
import { getDateFilter, getGroupFilter, formatStartEndDates, processDate, processGroup, composeFilterString } from '../utils/analytics-utils'
import { addQuery } from '../utils/router-utils'
import { useLocation, useNavigate } from 'react-router'

/**
 * @func getTitle
 * @param {Object} content
 * @param {Object} defaultTitle
 * @returns {Object}
 */
const getTitle = (content, defaultTitle) => {
  if (content._id !== '0') {
    return content.title
  }
  return defaultTitle
}

function Overview () {
  const [updated, setUpdated] = useState(false)
  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const id = useSelector(state => state.account.ID)
  const overview = useSelector(state => state.overview)
  const monthlyActiveUsers = useSelector(state => state.monthlyActiveUsers)
  const dateFilterStore = useSelector(state => state.dateFilter)
  const profiles = useSelector(state => state.filterSources.profiles)
  const name = 'Overview'

  let dateFilterFromStore = getDateFilter(dateFilterStore, name)
  dateFilterFromStore = dateFilterFromStore ? dateFilterFromStore.dateFilter || 'Week' : 'Week'
  const [dateFilter, setDateFilter] = useState(dateFilterFromStore)

  const groupFilter = getGroupFilter(dateFilterStore, name) || ''
  const dateInterval = dateFilter ? dateFilter.dateInterval || null : null

  const mostViewedContent = overview.mostViewedContent
  const mostViewedContentTitle = getTitle(mostViewedContent, 'No content has been viewed in news feed')
  const mostViewedContentTotalViews = mostViewedContent ? mostViewedContent.totalViews : 0
  const mostViewedContentId = mostViewedContent ? mostViewedContent._id : '0'

  const mostLikedContent = overview.mostLikedContent
  const mostLikedContentTitle = getTitle(mostLikedContent, 'No content has been liked')
  const mostLikedContentTotalLikes = mostLikedContent ? mostLikedContent.totalLikes : 0
  const mostLikedContentId = mostLikedContent ? mostLikedContent._id : '0'

  const mostReactedContent = overview.mostReactedContent
  const mostReactedContentTitle = getTitle(mostReactedContent, 'No content has been reacted')
  const mostReactedContentTotalReactions = mostReactedContent ? mostReactedContent.totalReactions : 0
  const mostReactedContentId = mostReactedContent ? mostReactedContent._id : '0'
  const mostReactedReactionCounts = mostReactedContent ? mostReactedContent.reactionCounts : []

  const mostCommentedContent = overview.mostCommentedContent
  const mostCommentedContentTitle = getTitle(mostCommentedContent, 'No content has been commented on')
  const mostCommentedContentTotalComments = mostCommentedContent ? mostCommentedContent.totalComments : 0
  const mostCommentedContentId = mostCommentedContent ? mostCommentedContent._id : '0'

  const loadingMonthlyActiveUsers = monthlyActiveUsers ? monthlyActiveUsers.loading : true

  const loading = overview.loading

  useEffect(() => {
    dispatch(componentCreated(name))
  }, [])

  useEffect(() => {
    if (profiles.length < 1 || profiles[0]._id === '0') {
      dispatch(fetchProfiles(id, {}))
      dispatch(fetchGroups(id, {}))
    }
  }, [])

  useEffect(() => {
    if (profiles.length > 0 && profiles[0]._id !== '0' && !updated) {
      let newDateFilter
      if (dateFilter === 'Custom' && dateInterval) {
        newDateFilter = dateInterval
      } else {
        newDateFilter = dateFilter
      }
      update(processDate(newDateFilter, id), processGroup(groupFilter))
      setUpdated(true)
    }
  }, [profiles, updated])

  useEffect(() => {
    if (id) {
      update(processDate(dateFilter, id), processGroup(groupFilter), id)
    }
  }, [])

  useEffect(() => {
    newQuery(location.search.substring(1).split('&'))
  }, [])

  const newQuery = (query) => {
    const qobj = {}
    let newDateFilter
    query.forEach((q) => {
      qobj[q.split('=')[0]] = q.split('=')[1]
    })
    if (dateFilter === 'Custom' && dateInterval) {
      newDateFilter = dateInterval
    } else {
      newDateFilter = dateFilter
    }
    update(processDate(newDateFilter), processGroup(groupFilter), id)
  }

  const update = (date, group, newID) => {
    const ID = newID || id
    const formattedDate = formatStartEndDates(date)
    if (!ID) return
    dispatch(fetchOverview(ID, formattedDate.start, formattedDate.end, { group: group.groups, profile: group.profiles }))
    dispatch(fetchMonthlyActiveUsers(ID, { group: group.groups, profile: group.profiles }))
  }

  const changeDateFilter = (newFilter, isCustom) => {
    if (isCustom) {
      dispatch(updateDateFilter(name, 'Custom', newFilter))
    } else {
      dispatch(updateDateFilter(name, newFilter))
    }
    const f = `${composeFilterString(groupFilter.profiles, groupFilter.groups)}_${groupFilter.alt || ''}`
    addQuery({ date: newFilter, filter: f }, location, navigate)
    setDateFilter(newFilter)
    if (newFilter !== 'Custom') {
      update(processDate(newFilter), processGroup(groupFilter))
    }
  }

  return (
    <div id='overviewActivity'>
      <div className='toolbar'>
        <SubHeader type='Overview' />
        <div className='reportButtons'>
          {/* <FavoriteButton /> */}
        </div>
      </div>
      <div className='DashboardContainer'>
        <MonthlyActiveUsers monthlyActiveUsers={monthlyActiveUsers} loading={loadingMonthlyActiveUsers} />
      </div>
      <div className='DashboardContainer'>
        <DateTimeFilter filterBy={changeDateFilter} currentFilter={dateFilter} />
        {(dateFilter !== 'Day' && dateFilter !== 'Week' && dateFilter !== 'Month') ? <CustomFilter filterBy={changeDateFilter} currentFilter={dateFilter} /> : ''}
        <div className='BlockContainer'>
          <ContentBlock
            loading={loading}
            contentId={mostViewedContentId}
            title={mostViewedContentTitle}
            totalViews={mostViewedContentTotalViews} />
          <AdministratorBlock
            loading={loading}
            title={overview.newContent}
            newContentPercent={overview.newContentChangePct}
            sinceText={dateDelta(dateFilter)} />
          <SocialBlock
            loading={loading}
            likedId={mostLikedContentId}
            reactedId={mostReactedContentId}
            commentedId={mostCommentedContentId}
            mostLikedPost={mostLikedContentTitle}
            newLikesPercent={mostLikedContentTotalLikes}
            accountDetails={ overview.accountDetails || {}}
            mostReactedPost={mostReactedContentTitle}
            newReactionsPercent={mostReactedContentTotalReactions}
            reactionCounts={mostReactedReactionCounts}
            mostCommentedPost={mostCommentedContentTitle}
            newCommentsPercent={mostCommentedContentTotalComments}
            sinceText={dateDelta(dateFilter)} />
          <IndividualBlock
            loading={loading}
            totalRegisteredPercent={(overview.absoluteTotalReg / overview.totalMembers)}
            newRegistrations={overview.registrationsChange}
            registeredUsers={overview.absoluteTotalReg}
            totalUsers={overview.totalMembers}
            totalLicenses={overview.totalLicenses}
            sinceText={dateDelta(dateFilter)}
          />
        </div>
      </div>
    </div>
  )
}

export default Overview
